import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CommunityPickerPage = () => {
  const navigate = useNavigate();
  const [associations, setAssociations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null); // association id being joined

  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async (term = "") => {
    setLoading(true);
    try {
      const params = term ? { search: term } : {};
      const res = await axios.get(`${API}/associations/public`, { params });
      setAssociations(res.data);
    } catch {
      toast.error("Impossible de charger les associations.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    fetchAssociations(val);
  };

  const handleJoin = async (assocId) => {
    setJoining(assocId);
    try {
      const token = localStorage.getItem("hafani_member_token");
      await axios.patch(
        `${API}/members/me/association`,
        { association_id: assocId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Vous avez rejoint la communauté !");
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "Une erreur est survenue.";
      toast.error(detail);
    } finally {
      setJoining(null);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex flex-col items-start group">
          <img
            src="/assets/logo.png"
            alt="Hafani"
            className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
          />
          <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
            Hafani, une solution pensée pour notre fin
          </span>
        </Link>
        <Link
          to="/"
          className="text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 group"
        >
          <span className="transition-transform group-hover:-translate-x-0.5">←</span> Accueil
        </Link>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 py-10 max-w-2xl mx-auto w-full">
        {/* Title */}
        <div className="text-center mb-10">
          <p className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-2">Étape 1 sur 2</p>
          <h1
            className="text-4xl text-slate-900 mb-3"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            Choisissez votre communauté
          </h1>
          <p className="text-slate-500 text-sm leading-relaxed">
            Trouvez l'association que vous souhaitez rejoindre pour continuer votre adhésion.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Rechercher par nom ou ville…"
            className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-slate-200 bg-white text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all shadow-sm"
          />
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin" />
          </div>
        ) : associations.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-sm">Aucune association trouvée.</p>
            {search && (
              <button
                onClick={() => { setSearch(""); fetchAssociations(""); }}
                className="mt-3 text-emerald-600 text-sm hover:underline"
              >
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {associations.map((assoc) => (
              <div
                key={assoc.id}
                className="bg-white rounded-2xl border border-slate-200/60 px-6 py-5 flex items-center justify-between gap-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="min-w-0">
                  <p className="text-slate-900 font-medium text-sm truncate">{assoc.name}</p>
                  {assoc.city && (
                    <p className="text-slate-400 text-xs flex items-center gap-1 mt-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {assoc.city}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleJoin(assoc.id)}
                  disabled={joining === assoc.id}
                  className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {joining === assoc.id ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin inline-block" />
                      Rejoindre…
                    </span>
                  ) : "Rejoindre"}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-slate-400 font-light">
          &copy; {new Date().getFullYear()} Hafani Management and Solutions
        </p>
      </footer>
    </div>
  );
};
